import React, { useState, useEffect, useCallback } from "react";
import { render } from "react-dom";
import { Product, ProductAccordion } from "../types/product";
import { ProductFormImages } from "../components/ProductImageSlideshow";
import { PaymentOptions } from "./PaymentOptions";
import * as currency from "@shopify/theme-currency";
import { getUrlWithVariant } from "@shopify/theme-product-form";

(function init() {
  const selectors = {
    root: "[data-product-form]",
  };

  function loadComponent() {
    const rootElement = document.querySelector(selectors.root);
    const variantId = rootElement?.getAttribute("data-variant-id");
    const subtitle = rootElement?.getAttribute("data-product-subtitle");
    const enableNotes = rootElement?.getAttribute("data-enable-notes");
    if (rootElement)
      render(
        <ProductForm
          variantId={variantId}
          subtitle={subtitle}
          enableNotes={enableNotes === "true"}
        />,
        rootElement
      );
  }

  document.addEventListener("shopify:section:load", loadComponent);
  loadComponent();
})();

function ProductForm({ variantId, subtitle, enableNotes }) {
  const [variant, setVariant] = useState(null);
  const product: Product = JSON.parse(
    document.querySelector("[data-product]").innerHTML
  );
  const accordions: ProductAccordion[] = JSON.parse(
    document.querySelector("[data-accordions]").innerHTML
  );

  const handleVariantChange = useCallback((variantId: string) => {
    const id = parseInt(variantId, 10);
    const variant = product.variants.find((el) => el.id === id);
    if (variant) setVariant(variant);
  }, []);

  useEffect(() => {
    if (variantId) handleVariantChange(variantId);
  }, []);

  useEffect(() => {
    const url = getUrlWithVariant(window.location.href, variant?.id);
    window.history.replaceState({ path: url }, "", url);
  }, [variant]);

  return (
    <div className="max-w-[1264px] mx-auto md:grid md:grid-cols-2 lg:grid-cols-5 items-start gap-[3rem] lg:pt-[3rem] pb-[6rem] md:px-[1rem] md:pt-[2rem]">
      <div className="md:col-span-1 lg:col-span-3 md:sticky md:top-[1rem]">
        {variant && <ProductFormImages variantId={variant.id} />}
      </div>
      <div className="md:col-span-1 lg:col-span-2">
        <div className="px-[1rem] md:px-0">
          <h1 className="mt-[1.5rem] md:mt-0 new-h2">{product.title}</h1>
          {subtitle && (
            <h2 className="text-sm font-bold mt-[0.5rem]">{subtitle}</h2>
          )}
          {product.description && (
            <div
              className="text-sm pt-[0.5rem] lg:pt-[1rem] leading-6"
              dangerouslySetInnerHTML={{ __html: product.description }}
            ></div>
          )}
          <form
            method="post"
            action="/cart/add"
            id=""
            accept-charset="UTF-8"
            encType="multipart/form-data"
            data-type="add-to-cart-form"
            className="mt-8"
          >
            <input type="hidden" name="form_type" value="product" />
            <input type="hidden" name="utf8" value="✓" />
            <input
              type="hidden"
              name="quantity"
              min="1"
              value="1"
              className="w-full mb-2"
            />

            <label
              htmlFor="product-{{ product.id }}"
              className="text-xs mb-2 block"
            >
              Select an option:
            </label>
            <select
              id="product-{{ product.id }}"
              name="id"
              className="w-full mb-4"
              onChange={(e) => handleVariantChange(e.target.value)}
              value={variant && variant.id}
            >
              {product.variants.map((el) => {
                return (
                  <option value={el.id}>
                    {el.title} -{" "}
                    {currency.formatMoney(el.price, "${{amount_no_decimals}}")}
                  </option>
                );
              })}
            </select>

            {enableNotes && (
              <>
                <label
                  htmlFor="product-{{ product.id }}-notes"
                  className="text-xs mb-2 block"
                >
                  Additional request?
                </label>
                <textarea
                  name="properties[Additional request]"
                  id="product-{{ product.id }}-notes"
                  rows={2}
                  className="w-full"
                ></textarea>
              </>
            )}

            <input
              type="submit"
              value="Add to cart"
              className="block mt-4 bg-black text-white font-bold rounded uppercase text-sm w-full px-3 py-[1.125rem]"
            />
          </form>

          <div className="text-center mt-2">
            <PaymentOptions />
          </div>

          {accordions.length > 0 && (
            <div className="bg-taupe-200 md:rounded-xl pb-[1rem] md:pb-0 px-[2.25rem] mt-[3rem]">
              {accordions.map((el, i) => {
                return (
                  <div
                    className={`${i > 0 ? "border-t border-taupe-600" : ""}`}
                  >
                    <details
                      className="list-none py-[1.75rem]"
                      id={`product-accordion-${i}`}
                    >
                      <summary
                        className="list-none font-bold text-xl flex items-center justify-between leading-4"
                        role="button"
                        aria-expanded="false"
                        aria-controls={`product-accordion-${i}-content`}
                      >
                        {el.title}
                        <svg
                          aria-hidden="true"
                          focusable="false"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.9997 13.1722L16.9497 8.22217L18.3637 9.63617L11.9997 16.0002L5.63574 9.63617L7.04974 8.22217L11.9997 13.1722Z"
                            fill="black"
                          />
                        </svg>
                      </summary>
                      <div
                        className="text-sm mt-[1rem] leading-6"
                        id={`product-accordion-${i}-content`}
                        dangerouslySetInnerHTML={{ __html: el.text }}
                      ></div>
                    </details>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
